const MAX_ACTIVE_MS = 14_400_000;

function startBirthchartReadingTime(nonce, env) {
    if (!/^[a-f0-9]{64}$/.test(nonce || '')) return () => {};
    const key = `am-reading:${nonce}`;
    const readSaved = () => {
        try { return JSON.parse(env.storage.getItem(key)); } catch { /* Storage is optional. */ }
    };
    const saved = readSaved();
    const visit = /^[a-f0-9]{32}$/.test(saved?.visit || '') ? saved.visit : env.newVisit();
    let activeMs = saved?.visit === visit && Number.isFinite(saved.activeMs)
        ? Math.max(0, Math.min(MAX_ACTIVE_MS, saved.activeMs)) : 0;
    let lastTick = env.now();
    let lastActivity = lastTick;
    let suspended = false;
    const isActive = () => !suspended && env.document.visibilityState === 'visible' && env.document.hasFocus();
    let active = isActive();
    let acknowledged = -1;
    const reconcile = () => {
        const current = readSaved();
        if (current?.visit === visit && Number.isFinite(current.activeMs)) {
            activeMs = Math.max(activeMs, Math.min(MAX_ACTIVE_MS, current.activeMs));
        }
    };
    const tick = () => {
        const now = env.now();
        if (active) activeMs = Math.min(MAX_ACTIVE_MS, activeMs + Math.min(2000,
            Math.max(0, Math.min(now, lastActivity + 30000) - lastTick)));
        lastTick = now;
    };
    const flush = (beacon = false) => {
        // Another chapter may have advanced this visit while this page was cached.
        reconcile();
        const value = Math.round(activeMs);
        try { env.storage.setItem(key, JSON.stringify({visit, activeMs: value})); } catch { /* Best effort. */ }
        if (value <= acknowledged) return;
        const body = JSON.stringify({nonce, visit, active_ms: value});
        try {
            if (beacon && env.beacon('/api/telemetry/birthchart-reading', new Blob([body], {type: 'application/json'}))) return;
            void env.fetch('/api/telemetry/birthchart-reading', {
                method: 'POST', credentials: 'same-origin', keepalive: true,
                referrerPolicy: 'no-referrer', headers: {'Content-Type': 'application/json'}, body,
            }).then(response => { if (response.ok) acknowledged = Math.max(acknowledged, value); }).catch(() => {});
        } catch { /* Reading must work without telemetry. */ }
    };
    const activity = () => {
        tick();
        active = isActive();
        if (active) lastActivity = env.now();
    };
    const visibility = () => {
        tick();
        active = isActive();
        if (active) lastActivity = env.now();
        else flush(true);
    };
    const hide = () => { tick(); suspended = true; active = false; flush(true); };
    const show = () => {
        reconcile();
        suspended = false;
        lastTick = env.now();
        visibility();
    };
    const events = ['keydown', 'pointerdown', 'pointermove', 'scroll', 'touchstart'];
    for (const event of events) env.window.addEventListener(event, activity, {passive: true});
    env.document.addEventListener('visibilitychange', visibility);
    env.window.addEventListener('focus', visibility);
    env.window.addEventListener('blur', visibility);
    env.window.addEventListener('pagehide', hide);
    env.window.addEventListener('pageshow', show);
    let elapsed = 0;
    const timer = env.window.setInterval(() => {
        tick(); active = isActive();
        if (++elapsed % 15 === 0) flush();
    }, 1000);
    flush();
    return () => {
        hide();
        env.window.clearInterval(timer);
        for (const event of events) env.window.removeEventListener(event, activity);
        env.document.removeEventListener('visibilitychange', visibility);
        env.window.removeEventListener('focus', visibility);
        env.window.removeEventListener('blur', visibility);
        env.window.removeEventListener('pagehide', hide);
        env.window.removeEventListener('pageshow', show);
    };
}

if (typeof document !== 'undefined') {
    const init = () => {
        const nonce = document.querySelector('meta[name="birthchart-reading-nonce"]')?.content;
        if (!nonce) return;
        try {
            startBirthchartReadingTime(nonce, {
                document, window, storage: window.sessionStorage, now: () => performance.now(),
                newVisit: () => Array.from(crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join(''),
                fetch: (...args) => window.fetch(...args),
                beacon: (...args) => navigator.sendBeacon?.(...args) ?? false,
            });
        } catch { /* Unavailable browser APIs do not interrupt the report. */ }
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
    else init();
}
if (typeof module !== 'undefined') module.exports = {startBirthchartReadingTime};
